/**
 * PieceRulesAnimator - Comprehensive animation system for piece rules demonstrations
 *
 * This class provides a centralized system for creating educational animations
 * that demonstrate piece movements, captures, and special abilities.
 *
 * Features:
 * - Piece placement with FEN loading
 * - Selection animations
 * - Movement demonstrations with timing
 * - Capture animations with red glow effects
 * - Sound effects for placement and movement
 * - Player input blocking during animations
 * - Only active in Rules game mode
 */

import { ZodiacEngine } from "../../services/zodiacEngine/zodiacEngine";
import {
  Mode,
  Position,
  GameState,
  IPiece,
  IMove,
  MoveType,
  ISideEffect,
  Effect,
  EffectType,
} from "../../services/zodiacEngine/types";
import { loadTestScenarioFEN } from "../../services/zodiacEngine/Fen/FenLoader";

export interface AnimationStep {
  type:
    | "placement"
    | "select"
    | "move"
    | "capture"
    | "deselect"
    | "wait"
    | "click"
    | "rollover"
    | "transform"
    | "sideeffect";
  data?: {
    fen?: string;
    fromPosition?: Position;
    toPosition?: Position;
    capturePosition?: Position;
    duration?: number;
    soundEffect?: "placement" | "movement" | "capture" | "none";
    screenPosition?: { x: number; y: number };
    targetPiece?: string; // e.g. "light-bear", "dark-crane"
    suppressPieceSelection?: boolean; // Prevent piece selection during click animations
    transformPosition?: Position; // Position of piece to transform
    newPieceType?: string; // What to transform the piece into (e.g. "crane")
    newPieceIsLight?: boolean; // Whether the new piece is light or dark
    sideEffectPosition?: Position; // Position to apply side effect
    sideEffectType?: string; // Type of side effect (e.g. "entangle")
    moveType?: MoveType; // Specific move type to use (e.g. MoveType.ink)
  };
}

export interface AnimationSequence {
  name: string;
  description: string;
  steps: AnimationStep[];
}

// Interface for selected piece
interface SelectedPiece {
  piece: IPiece;
  position: Position;
}

export class PieceRulesAnimator {
  private engine: ZodiacEngine;
  private updateGameState: (gameState: GameState) => void;
  private updateSelectedPiece: (selectedPiece?: SelectedPiece) => void;
  private updatePotentialMoves: (potentialMoves: IMove[]) => void;
  private updatePotentialSideEffects?: (
    potentialSideEffects: ISideEffect[]
  ) => void;
  private updateMoveDescription?: (moveDescription: string | null) => void;
  private onPostMoveCallback?: () => void;
  private onCompleteCallback?: () => void;
  private isAnimating: boolean = false;
  private currentSequence: AnimationSequence | null = null;
  private currentStepIndex: number = 0;
  private selectedSquare: Position | null = null;
  private gameMode: Mode;
  private cancelRequested: boolean = false;
  private currentPostMoveDescription: string | null = null;

  // Sound effects
  private placementSound: HTMLAudioElement;
  private movementSound: HTMLAudioElement;
  private clickSound: HTMLAudioElement;

  constructor(
    engine: ZodiacEngine,
    updateGameState: (gameState: GameState) => void,
    gameMode: Mode,
    updateSelectedPiece: (selectedPiece?: SelectedPiece) => void,
    updatePotentialMoves: (potentialMoves: IMove[]) => void,
    updatePotentialSideEffects?: (potentialSideEffects: ISideEffect[]) => void,
    updateMoveDescription?: (moveDescription: string | null) => void,
    onPostMoveCallback?: () => void
  ) {
    this.engine = engine;
    this.updateGameState = updateGameState;
    this.updateSelectedPiece = updateSelectedPiece;
    this.updatePotentialMoves = updatePotentialMoves;
    this.updatePotentialSideEffects = updatePotentialSideEffects;
    this.updateMoveDescription = updateMoveDescription;
    this.onPostMoveCallback = onPostMoveCallback;
    this.gameMode = gameMode;

    // Initialize sound effects
    this.placementSound = new Audio("/turn change.mp3");
    this.placementSound.volume = 0.5;

    this.movementSound = new Audio("/turn change.mp3"); // Using same sound for now
    this.movementSound.volume = 0.3;

    this.clickSound = new Audio("/beep-313342.mp3");
    this.clickSound.volume = 0.1;

  }

  /**
   * Check if animator should be active (only in Rules mode)
   */
  private isActive(): boolean {
    return this.gameMode === Mode.Rules;
  }

  /**
   * Set animation complete callback (for mobile auto-back)
   */
  public setOnCompleteCallback(callback: () => void): void {
    this.onCompleteCallback = callback;
  }

  /**
   * Play animation sequence for piece demonstrations
   */
  async playSequence(sequence: AnimationSequence): Promise<void> {
    if (!this.isActive()) {
      return;
    }

    // PERFECT RESET: Always stop current animation and clear state completely

    // Force stop any current animation (even if not detected as running)
    this.stopAnimation();

    // Double-check: prevent starting if somehow still animating
    if (this.isAnimating) {
      return;
    }

    // Wait for any cleanup to complete
    await this.delay(100);

    this.isAnimating = true;
    this.currentSequence = sequence;
    this.currentStepIndex = 0;
    this.cancelRequested = false;

    // Don't clear post-move description here - it should be set by caller before playSequence

    // Show initial description at start of animation
    if (this.updateMoveDescription && sequence.description) {
      this.updateMoveDescription(sequence.description);
    }

    try {
      for (let i = 0; i < sequence.steps.length; i++) {
        // Check for cancellation before each step
        if (this.cancelRequested) {
          break;
        }

        this.currentStepIndex = i;
        await this.executeStep(sequence.steps[i]);
      }
    } catch (error) {
    } finally {
      this.isAnimating = false;
      this.currentSequence = null;
      this.currentStepIndex = 0;
      this.selectedSquare = null;
      this.cancelRequested = false;

      // Call completion callback if provided (for mobile auto-back)
      if (this.onCompleteCallback) {
        this.onCompleteCallback();
      }

      // Keep postMoveDescription visible after animation ends
      // (Don't clear it so user can read the explanation)

    }
  }

  /**
   * Execute a single animation step with cancellation checks
   */
  private async executeStep(step: AnimationStep): Promise<void> {
    // Check for cancellation before executing any step
    if (this.cancelRequested) {
      return;
    }

    switch (step.type) {
      case "placement":
        await this.executePlacement(step);
        break;
      case "select":
        await this.executeSelection(step);
        break;
      case "move":
        await this.executeMovement(step);
        break;
      case "capture":
        await this.executeCapture(step);
        break;
      case "deselect":
        await this.executeDeselection(step);
        break;
      case "wait":
        await this.executeWait(step);
        break;
      case "click":
        await this.executeClickAnimation(step);
        break;
      case "rollover":
        await this.executeRolloverAnimation(step);
        break;
      case "transform":
        await this.executeTransform(step);
        break;
      case "sideeffect":
        await this.executeSideEffect(step);
        break;
      default:
    }

    // Check for cancellation after executing step
    if (this.cancelRequested) {
      return;
    }
  }

  /**
   * Load a FEN position and play placement sound
   */
  private async executePlacement(step: AnimationStep): Promise<void> {
    if (this.cancelRequested) return;

    if (!step.data?.fen) {
      throw new Error("Placement step requires FEN string");
    }


    // Load the FEN position
    loadTestScenarioFEN(this.engine, step.data.fen);

    // Update the React game state and preserve Rules mode
    const newGameState = this.engine.getGameState();
    newGameState.mode = Mode.Rules; // Force Rules mode after FEN loading
    this.updateGameState(newGameState);

    // Coordinate labels removed - animation system working correctly
    // setTimeout(() => {
    //   this.labelAllSquares();
    // }, 100);

    // Play placement sound
    if (step.data.soundEffect === "placement" || !step.data.soundEffect) {
      try {
        await this.placementSound.play();
      } catch (error) {
      }
    }

    // Brief delay after placement
    await this.delay(500);
  }

  /**
   * Select a piece at the specified position
   */
  private async executeSelection(step: AnimationStep): Promise<void> {
    if (this.cancelRequested) return;

    if (!step.data?.fromPosition) {
      throw new Error("Selection step requires fromPosition");
    }


    this.selectedSquare = step.data.fromPosition;

    // Add selection highlight to the game state (this would need UI integration)
    this.addSelectionHighlight(step.data.fromPosition);

    await this.delay(step.data.duration || 800);
  }

  /**
   * Execute a movement animation
   */
  private async executeMovement(step: AnimationStep): Promise<void> {
    if (this.cancelRequested) return;

    if (!step.data?.fromPosition || !step.data?.toPosition) {
      throw new Error("Movement step requires fromPosition and toPosition");
    }


    // Get the current game state
    const gameState = this.engine.getGameState();

    // Get the piece at the source position
    const fromSquare =
      gameState.board[step.data.fromPosition.row][step.data.fromPosition.col];
    if (!fromSquare || !fromSquare.Pieces || fromSquare.Pieces.length === 0) {
      return;
    }

    // VIRUS CONTAGION SPECIAL CASE: For the second move from (3,0) to (5,0), find the Virus piece specifically
    let piece = fromSquare.Pieces[0]; // Default: take the first piece on the square
    if (step.data.fromPosition.row === 3 && step.data.fromPosition.col === 0 &&
        step.data.toPosition.row === 5 && step.data.toPosition.col === 0) {
      const virusPiece = fromSquare.Pieces.find(p => p.type === "Virus");
      if (virusPiece) {
        piece = virusPiece;
      } else {
      }
    }

    // Get the piece at the destination position (if any)
    const toSquare =
      gameState.board[step.data.toPosition.row][step.data.toPosition.col];
    const toPiece =
      toSquare && toSquare.Pieces && toSquare.Pieces.length > 0
        ? toSquare.Pieces[0]
        : null;

    // Show movement highlighting
    this.addMovementHighlight(step.data.fromPosition, step.data.toPosition);

    // Play movement sound
    if (
      step.data.soundEffect === "movement" ||
      (!step.data.soundEffect && step.data.soundEffect !== "none")
    ) {
      try {
        await this.movementSound.play();
      } catch (error) {
      }
    }

    // Wait for visual effect
    await this.delay((step.data.duration || 1200) * 0.7);

    // Execute the actual move through the engine
    try {
      if (!step.data?.fromPosition || !step.data?.toPosition) {
        throw new Error("Movement step requires fromPosition and toPosition");
      }

      const fromPosition = step.data.fromPosition;
      const toPosition = step.data.toPosition;


      // Get valid moves to find the correct move type (capture vs regular vs ability)
      const validMoves = this.engine.getValidMoves(fromPosition, piece);

      let targetMove;
      if (step.data?.moveType) {
        // If specific moveType is requested, filter to only that type
        targetMove = validMoves.find(move =>
          move.position.row === toPosition.row &&
          move.position.col === toPosition.col &&
          move.moveType === step.data!.moveType
        );
      } else {
        // Default behavior: find any move to the target position
        targetMove = validMoves.find(move =>
          move.position.row === toPosition.row &&
          move.position.col === toPosition.col
        );
      }

      if (targetMove) {
        const moveSuccess = await this.engine.makeMove(
          fromPosition,
          toPosition,
          piece,
          toPiece,
          targetMove.moveType,
          targetMove.sideEffects
        );

        if (moveSuccess) {
          // Update the React game state
          const newGameState = this.engine.getGameState();
          this.updateGameState(newGameState);
        } else {
        }
      } else {

        // VIRUS CONTAGION OVERRIDE: Manually force the second move from (3,0) to (5,0)
        // Accept any piece type since virus infection may have changed the piece
        if (fromPosition.row === 3 && fromPosition.col === 0 &&
            toPosition.row === 5 && toPosition.col === 0) {

          // Use appropriate move type based on the actual piece type and target
          let moveType = MoveType.regular;
          if (toPiece && toPiece.isLight !== piece.isLight) {
            moveType = MoveType.capture;
          } else if (piece.type === "Virus") {
            moveType = MoveType.infect;
          }


          // Manually force the move through the engine without validation
          const moveSuccess = await this.engine.makeMove(
            fromPosition,
            toPosition,
            piece,
            toPiece,
            moveType,
            []
          );

          if (moveSuccess) {
            const newGameState = this.engine.getGameState();
            this.updateGameState(newGameState);
          } else {
          }
        } else {
        }
        // Determine appropriate fallback move type based on destination
        let fallbackMoveType = MoveType.regular;
        if (toPiece && toPiece.isLight !== piece.isLight) {
          // There's an enemy piece at destination, use capture
          fallbackMoveType = MoveType.capture;
        }

        const moveSuccess = await this.engine.makeMove(
          fromPosition,
          toPosition,
          piece,
          toPiece,
          fallbackMoveType,
          []
        );

        if (moveSuccess) {
          // Update the React game state
          const newGameState = this.engine.getGameState();
          this.updateGameState(newGameState);
        } else {
        }
      }
    } catch (error) {
    }

    // Wait for remaining duration
    await this.delay((step.data.duration || 1200) * 0.3);

    // Trigger post-move callback to show additional description
    if (this.onPostMoveCallback && this.currentPostMoveDescription) {
      this.onPostMoveCallback();
    }
  }

  /**
   * Execute a capture animation with red glow effect
   */
  private async executeCapture(step: AnimationStep): Promise<void> {
    if (this.cancelRequested) return;

    if (!step.data?.capturePosition) {
      throw new Error("Capture step requires capturePosition");
    }


    // Add red glow capture effect
    this.addCaptureHighlight(step.data.capturePosition);

    await this.delay(step.data.duration || 1000);
  }

  /**
   * Remove selection highlighting
   */
  private async executeDeselection(step: AnimationStep): Promise<void> {
    if (this.cancelRequested) return;


    this.selectedSquare = null;
    this.removeAllHighlights();

    await this.delay(step.data?.duration || 300);
  }

  /**
   * Wait for specified duration
   */
  private async executeWait(step: AnimationStep): Promise<void> {
    if (this.cancelRequested) return;

    const duration = step.data?.duration || 1000;
    await this.delay(duration);
  }

  /**
   * Execute click animation at board position or screen coordinates
   * This also selects the piece through the engine to show natural selection highlights
   */
  private async executeClickAnimation(step: AnimationStep): Promise<void> {
    if (this.cancelRequested) return;

    let x = 0,
      y = 0;
    let piecePosition: Position | null = null;

    if (step.data?.targetPiece) {
      // Find the target piece on the board and get its screen position
      piecePosition = this.findPiecePosition(step.data.targetPiece);
      if (piecePosition) {
        const screenPos = this.boardPositionToScreenCoords(piecePosition);
        x = screenPos.x;
        y = screenPos.y;
      } else {
        throw new Error(
          `Target piece ${step.data.targetPiece} not found on board`
        );
      }
    } else if (step.data?.fromPosition) {
      // Convert board position to screen coordinates
      piecePosition = step.data.fromPosition;
      const screenPos = this.boardPositionToScreenCoords(piecePosition);
      x = screenPos.x;
      y = screenPos.y;
    } else if (step.data?.screenPosition) {
      // Use provided screen coordinates
      x = step.data.screenPosition.x;
      y = step.data.screenPosition.y;
    } else {
      throw new Error(
        "Click animation requires either targetPiece, fromPosition, or screenPosition"
      );
    }


    // Play click sound effect
    try {

      // Reset the audio to start from beginning
      this.clickSound.currentTime = 0;

      const playPromise = this.clickSound.play();
      await playPromise;
    } catch (error) {
    }

    // Create click animation DOM element
    this.createClickAnimation(x, y);

    // If we have a piece position and piece selection is not suppressed, select it through the engine
    if (piecePosition && !step.data?.suppressPieceSelection) {
      await this.selectPieceWithEngine(piecePosition);
    } else if (step.data?.suppressPieceSelection) {
    }

    await this.delay(2000); // Even slower duration for better visibility
  }

  /**
   * Execute transform animation to change a piece type
   */
  private async executeTransform(step: AnimationStep): Promise<void> {
    if (this.cancelRequested) return;

    if (
      !step.data?.transformPosition ||
      !step.data?.newPieceType ||
      step.data?.newPieceIsLight === undefined
    ) {
      throw new Error(
        "Transform step requires transformPosition, newPieceType, and newPieceIsLight"
      );
    }

    const position = step.data.transformPosition;
    const newPieceType = step.data.newPieceType;
    const isLight = step.data.newPieceIsLight;


    // Get the current game state
    const gameState = this.engine.getGameState();

    // Get the square at the transform position
    const square = gameState.board[position.row][position.col];
    if (!square || !square.Pieces || square.Pieces.length === 0) {
      return;
    }

    // Transform the piece by directly modifying its type
    const piece = square.Pieces[0];

    // Store original properties
    const originalType = piece.type;
    const originalIsLight = piece.isLight;

    // Transform the piece
    (piece as any).type = newPieceType;
    (piece as any).isLight = isLight;

    // Apply CSS style override to remove zombified coloring
    this.applyZombificationRemovalCSS(position, step);

    // Update the React game state to reflect the transformation
    const newGameState = this.engine.getGameState();
    newGameState.mode = Mode.Rules; // Ensure Rules mode is preserved
    this.updateGameState(newGameState);


    // Brief delay to show the transformation
    await this.delay(step.data?.duration || 800);
  }

  /**
   * Apply CSS override to remove zombified coloring from a piece
   */
  private applyZombificationRemovalCSS(
    position: Position,
    step: AnimationStep
  ): void {

    // Find the square element at this position
    const squareElement = this.findSquareElement(position);
    if (!squareElement) {
      return;
    }

    // Find the piece image within this square
    const pieceImage = squareElement.querySelector("img") as HTMLImageElement;
    if (!pieceImage) {
      return;
    }

    // Create and inject CSS to override the zombified coloring
    const styleId = "zombification-removal-override";
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;

    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    // Add aggressive CSS rules to remove ALL possible zombified styling
    const cssRule = `
      /* Target all possible selectors for crane pieces */
      img[src*="crane"],
      img[src*="crane-dark"],
      img[src*="crane-light"],
      [class*="crane"] img,
      [class*="piece"] img[src*="crane"] {
        filter: none !important;
        opacity: 1 !important;
        background: none !important;
        background-color: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
        backdrop-filter: none !important;
        transform: none !important;
        mix-blend-mode: normal !important;
      }

      /* Remove any overlay or pseudo-element effects */
      img[src*="crane"]::before,
      img[src*="crane"]::after {
        display: none !important;
      }

      /* Target specific zombified classes if they exist */
      .zombified img,
      .zombie img,
      [class*="zombi"] img {
        filter: none !important;
        background: none !important;
        opacity: 1 !important;
      }
    `;

    styleElement.textContent = cssRule;

    // Apply very aggressive direct styles to the piece image
    const aggressiveStyles = {
      filter: "none",
      opacity: "1",
      background: "none",
      backgroundColor: "transparent",
      border: "none",
      boxShadow: "none",
      outline: "none",
      backdropFilter: "none",
      transform: "none",
      mixBlendMode: "normal",
    };

    Object.assign(pieceImage.style, aggressiveStyles);

    // Also try to remove any CSS classes that might be causing zombified appearance
    const classesToRemove = [
      "zombified",
      "zombie",
      "controlled",
      "enemy-controlled",
    ];
    classesToRemove.forEach((className) => {
      pieceImage.classList.remove(className);
      if (pieceImage.parentElement) {
        pieceImage.parentElement.classList.remove(className);
      }
    });

    // NUCLEAR OPTION: Force change the image source if it's still showing zombified
    if (
      pieceImage.src &&
      pieceImage.src.includes("crane") &&
      step.data?.newPieceType &&
      step.data?.newPieceIsLight !== undefined
    ) {
      // Force reload the normal crane image
      const normalCraneSrc = `/Icons/${step.data.newPieceType}-${
        step.data.newPieceIsLight ? "light" : "dark"
      }.png`;

      // Change the src and force reload
      pieceImage.src = normalCraneSrc;

      // Force DOM update
      setTimeout(() => {
        pieceImage.src = normalCraneSrc + "?t=" + Date.now(); // Add timestamp to force reload
        Object.assign(pieceImage.style, aggressiveStyles); // Reapply styles after image change
      }, 100);
    }

  }

  /**
   * Execute side effect animation to show purple highlights
   */
  private async executeSideEffect(step: AnimationStep): Promise<void> {
    if (this.cancelRequested) return;

    if (!step.data?.sideEffectPosition || !step.data?.sideEffectType) {
      throw new Error(
        "Side effect step requires sideEffectPosition and sideEffectType"
      );
    }

    const position = step.data.sideEffectPosition;
    const effectType = step.data.sideEffectType;


    // Get the current game state and the piece at the position
    const gameState = this.engine.getGameState();
    const square = gameState.board[position.row][position.col];

    if (!square || !square.Pieces || square.Pieces.length === 0) {
      return;
    }

    const piece = square.Pieces[0];

    try {
      switch (effectType.toLowerCase()) {
        case "entangle":

          // Create a proper entangle side effect using the game engine
          const effect = Effect.Entangle();

          // Generate proper UUID format
          const generateUUID = () =>
            "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              const v = c === "x" ? r : (r & 0x3) | 0x8;
              return v.toString(16);
            });

          // Create a mock move that represents the entangle action
          const entangleMove: IMove = {
            id: generateUUID() as any,
            pieceId: piece.id,
            position: position,
            toPiece: piece,
            moveType: MoveType.regular, // Regular move with entangle side effect
            sideEffects: [
              {
                id: generateUUID() as any,
                moveId: generateUUID() as any,
                position: position,
                piecesToEffect: [piece],
                effect: effect,
              },
            ],
          };

          // Let the engine's movement manager handle the entangle side effects

          // Find the octopus piece that's doing the entangling (should be adjacent)
          const gameState = this.engine.getGameState();
          let octopusPiece: IPiece | null = null;

          // Look for octopus adjacent to the target position
          const adjacentPositions = [
            { row: position.row - 1, col: position.col },
            { row: position.row + 1, col: position.col },
            { row: position.row, col: position.col - 1 },
            { row: position.row, col: position.col + 1 },
          ];

          for (const adjPos of adjacentPositions) {
            if (
              adjPos.row >= 0 &&
              adjPos.row < 5 &&
              adjPos.col >= 0 &&
              adjPos.col < 8
            ) {
              const adjSquare = gameState.board[adjPos.row][adjPos.col];
              if (adjSquare?.Pieces?.[0]?.type === "Octopus") {
                octopusPiece = adjSquare.Pieces[0];
                break;
              }
            }
          }

          if (octopusPiece) {
            // Use the engine's proper entangle handling
            const movementManager = (this.engine as any).movementManager;
            if (movementManager && movementManager.handleEntangleSideEffects) {
              movementManager.handleEntangleSideEffects(
                octopusPiece,
                entangleMove.sideEffects
              );
            }
          }

          // Update the potential side effects for UI highlighting
          if (this.updatePotentialSideEffects) {
            this.updatePotentialSideEffects(entangleMove.sideEffects);
          }

          // Force game state update to reflect changes
          const updatedGameState = this.engine.getGameState();
          updatedGameState.mode = Mode.Rules;
          this.updateGameState(updatedGameState);

          break;

        default:
          return;
      }
    } catch (error) {
    }

    // Brief delay to show the side effect
    await this.delay(step.data?.duration || 1000);
  }

  /**
   * Clean up zombification CSS overrides
   */
  private cleanupZombificationCSS(): void {
    const styleElement = document.getElementById(
      "zombification-removal-override"
    );
    if (styleElement) {
      styleElement.remove();
    }
  }

  /**
   * Clean up persistent piece states like entangled status
   */
  private cleanupPersistentPieceStates(): void {
    // Note: Since we're now using the engine's proper entangle handling,
    // the engine should manage entangled states appropriately
  }

  /**
   * Execute rollover animation to show hover effect on a square
   */
  private async executeRolloverAnimation(step: AnimationStep): Promise<void> {
    if (this.cancelRequested) return;

    let position: Position;

    if (step.data?.toPosition) {
      position = step.data.toPosition;
    } else if (step.data?.fromPosition) {
      position = step.data.fromPosition;
    } else {
      throw new Error(
        "Rollover animation requires either fromPosition or toPosition"
      );
    }


    // Create rollover visual effect
    this.createRolloverEffect(position);

    // Wait for the specified duration or default
    const duration = step.data?.duration || 1000;
    await this.delay(duration);
  }

  /**
   * Create visual rollover effect on a square
   */
  private createRolloverEffect(position: Position): void {

    // Use the fixed findSquareElement method to get precise coordinates
    const squareElement = this.findSquareElement(position);

    if (!squareElement) {
      return;
    }

    // Get the square's screen coordinates
    const rect = squareElement.getBoundingClientRect();
    const screenPos = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };


    // Create rollover element
    const rolloverElement = document.createElement("div");
    rolloverElement.className = "rollover-highlight";
    rolloverElement.style.cssText = `
      position: fixed;
      left: ${screenPos.x - 30}px;
      top: ${screenPos.y - 30}px;
      width: 60px;
      height: 60px;
      border: 3px solid #ffeb3b;
      border-radius: 8px;
      background-color: rgba(255, 235, 59, 0.2);
      box-shadow: 0 0 15px rgba(255, 235, 59, 0.6);
      pointer-events: none;
      z-index: 1000;
      animation: rolloverPulse 0.8s ease-in-out infinite alternate;
    `;

    // Add CSS animation keyframes if not already added
    if (!document.querySelector("#rollover-style")) {
      const style = document.createElement("style");
      style.id = "rollover-style";
      style.textContent = `
        @keyframes rolloverPulse {
          from {
            opacity: 0.4;
            transform: scale(0.95);
          }
          to {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(rolloverElement);

    // Remove the rollover effect after a delay
    setTimeout(() => {
      if (rolloverElement.parentNode) {
        rolloverElement.parentNode.removeChild(rolloverElement);
      }
    }, 1500);
  }

  /**
   * Convert chess notation (A1, B3, etc.) to board position
   * Chess notation: A-H (columns), 1-8 (rows from bottom)
   * Board position: row 0-7 (top to bottom), col 0-7 (left to right)
   */
  public static chessNotationToPosition(notation: string): Position {
    if (notation.length !== 2) {
      throw new Error(
        `Invalid chess notation: ${notation}. Expected format like A3, B4, etc.`
      );
    }

    const col = notation.charAt(0).toUpperCase();
    const row = parseInt(notation.charAt(1));

    // Validate column (A-H)
    if (col < "A" || col > "H") {
      throw new Error(`Invalid column: ${col}. Must be A-H.`);
    }

    // Validate row (1-8)
    if (row < 1 || row > 8) {
      throw new Error(`Invalid row: ${row}. Must be 1-8.`);
    }

    // Convert to board coordinates
    // Chess: A=0, B=1, ..., H=7 (columns left to right)
    // Chess: 1=bottom, 8=top -> Board: 0=top, 7=bottom
    const boardCol = col.charCodeAt(0) - "A".charCodeAt(0); // A=0, B=1, etc.
    const boardRow = 8 - row; // Chess row 1 = board row 7, Chess row 8 = board row 0

    return { row: boardRow, col: boardCol };
  }

  /**
   * Convert board position to chess notation
   */
  public static positionToChessNotation(position: Position): string {
    const col = String.fromCharCode("A".charCodeAt(0) + position.col);
    const row = 8 - position.row; // Chess A1 is bottom-left, so flip the rows
    return `${col}${row}`;
  }

  /**
   * Find the actual DOM square element at a given board position
   * This provides more robust element detection than coordinate calculation
   */
  private findSquareElement(position: Position): HTMLElement | null {

    // Try multiple strategies to find the square element

    // Strategy 1: Look for Square component with key attribute
    const squareKey = `${position.row}-${position.col}`;
    let squareElement = document.querySelector(
      `[data-square-key="${squareKey}"]`
    ) as HTMLElement;

    if (squareElement) {
      return squareElement;
    }

    // Strategy 2: Look through all squares by position classes or attributes
    const allSquares = document.querySelectorAll(
      '[class*="square"], [class*="Square"]'
    ) as NodeListOf<HTMLElement>;
    for (const square of allSquares) {
      // Check if square has position data attributes
      const squareRow = square.getAttribute("data-row");
      const squareCol = square.getAttribute("data-col");

      if (
        squareRow === position.row.toString() &&
        squareCol === position.col.toString()
      ) {
        return square;
      }
    }

    // Strategy 3: Calculate based on board structure
    // Look for the board container and calculate position
    const boardElement = document.querySelector(
      '[class*="board"], [class*="Board"]'
    ) as HTMLElement;
    if (boardElement) {
      const rows = boardElement.querySelectorAll(
        '[class*="row"], [class*="boardrow"]'
      ) as NodeListOf<HTMLElement>;

      // COPY THE YELLOW TAGS: Use same flipped row indexing
      // Yellow tags use: flippedRowIndex = 7 - rowIndex
      // So to find DOM row from position.row: domRowIndex = 7 - position.row
      const boardHeight = rows.length;
      const visualRowIndex = 7 - position.row; // FLIP TO MATCH YELLOW TAGS


      if (rows[visualRowIndex]) {
        const squares = rows[visualRowIndex].querySelectorAll(
          '[class*="square"], [class*="Square"]'
        ) as NodeListOf<HTMLElement>;

        // Apply the same A X B X C X D X E pattern fix as in coordinate labeling
        const gameSquares = Array.from(squares)
          .filter((_, domIndex) => domIndex % 2 === 0)
          .slice(0, 5);

        // MANUAL SOLUTION: Use normal doubling for all animations
        // This maps: [4,0]→DOM[0], [4,1]→DOM[2], [4,2]→DOM[4], [4,3]→DOM[6], [4,4]→DOM[8]
        const targetDOMIndex = position.col * 2;


        if (
          targetDOMIndex >= 0 &&
          targetDOMIndex < squares.length &&
          squares[targetDOMIndex]
        ) {
          const targetSquare = squares[targetDOMIndex];
          return targetSquare;
        } else {
        }

        // Fallback to filtered squares method if manual doubling fails
        const fallbackGameSquares = Array.from(squares)
          .filter((_, domIndex) => domIndex % 2 === 0)
          .slice(0, 5);
        const correctArrayIndex = position.col;
        if (
          correctArrayIndex >= 0 &&
          correctArrayIndex < fallbackGameSquares.length &&
          fallbackGameSquares[correctArrayIndex]
        ) {
          return fallbackGameSquares[correctArrayIndex];
        } else {
        }
      }
    }

    return null;
  }

  /**
   * Get precise screen coordinates for a square using actual DOM element detection
   */
  private getSquareScreenCoords(position: Position): { x: number; y: number } {
    const squareElement = this.findSquareElement(position);

    if (squareElement) {
      const rect = squareElement.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      return { x: centerX, y: centerY };
    }

    // Fallback to the existing method
    return this.boardPositionToScreenCoords(position);
  }

  /**
   * Select a piece through the engine to show natural selection highlights
   */
  private async selectPieceWithEngine(position: Position): Promise<void> {
    const gameState = this.engine.getGameState();
    const square = gameState.board[position.row][position.col];

    if (!square || !square.Pieces || square.Pieces.length === 0) {
      return;
    }

    // Get the first piece on the square (primary piece)
    const piece = square.Pieces[0];

    try {
      // Get potential moves for the piece using the engine
      const potentialMoves = this.engine.getValidMoves(position, piece);

      // Update the UI with the selected piece and its potential moves
      // This will trigger the natural selection highlights
      this.updateSelectedPiece({
        piece: piece,
        position: position,
      });

      this.updatePotentialMoves(potentialMoves);

      // Store the selected piece information for internal use
      this.selectedSquare = position;

    } catch (error) {
    }
  }

  /**
   * Find a specific piece on the current board
   */
  private findPiecePosition(targetPiece: string): Position | null {
    const gameState = this.engine.getGameState();
    const [side, pieceType] = targetPiece.split("-");
    const isLight = side === "light";

    // Search through the board for the piece
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const square = gameState.board[row][col];
        if (square && square.Pieces) {
          for (const piece of square.Pieces) {
            if (
              piece.isLight === isLight &&
              piece.type.toLowerCase() === pieceType.toLowerCase()
            ) {
              return { row, col };
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Convert board position to screen coordinates by finding the actual piece element
   */
  private boardPositionToScreenCoords(position: Position): {
    x: number;
    y: number;
  } {
    // Use the new robust square finding system
    return this.getSquareScreenCoords(position);
  }

  /**
   * Debug method: Label all squares with their coordinates
   */
  public labelAllSquares(): void {

    const boardElement = document.querySelector(
      '[class*="board"], [class*="Board"]'
    ) as HTMLElement;
    if (!boardElement) return;

    const rows = boardElement.querySelectorAll(
      '[class*="row"], [class*="boardrow"]'
    ) as NodeListOf<HTMLElement>;

    rows.forEach((row, rowIndex) => {
      const squares = row.querySelectorAll(
        '[class*="square"], [class*="Square"]'
      ) as NodeListOf<HTMLElement>;

      // MANUAL CHEESE SOLUTION: Iterate squares like A X B X C X D X E
      // Take every other square starting from index 0 to get the actual game squares
      const gameSquares = Array.from(squares)
        .filter((_, domIndex) => domIndex % 2 === 0)
        .slice(0, 5);


      // FORCE SEQUENTIAL COLUMN INDEXING: A=0, B=1, C=2, D=3, E=4
      gameSquares.forEach((square, colIndex) => {
        // Remove any existing labels
        const existingLabel = square.querySelector(".coord-label");
        if (existingLabel) {
          existingLabel.remove();
        }
        const existingSelectionLabel = square.querySelector(
          ".selection-coord-label"
        );
        if (existingSelectionLabel) {
          existingSelectionLabel.remove();
        }
        const existingTravelLabel = square.querySelector(".travel-coord-label");
        if (existingTravelLabel) {
          existingTravelLabel.remove();
        }

        // Create coordinate label (YELLOW - IMMUTABLE TRUTH)
        const label = document.createElement("div");
        label.className = "coord-label";
        label.style.cssText = `
          position: absolute;
          top: 5px;
          left: 5px;
          background: rgba(255, 255, 0, 0.8);
          color: black;
          font-size: 12px;
          font-weight: bold;
          padding: 2px 4px;
          border-radius: 3px;
          z-index: 1000;
          pointer-events: none;
          font-family: monospace;
        `;

        // Show both 0-indexed and chess notation
        // For a 5x8 board: rows 0-7, cols 0-4
        // Flip row index so 0,0 is at bottom-left (to match coordinate system)
        const flippedRowIndex = 7 - rowIndex;

        // Chess notation: A1 should be at bottom-left (flipped row 0, col 0)
        // ENSURE colIndex is 0-4 for A-E
        const chessCol = String.fromCharCode("A".charCodeAt(0) + colIndex);
        const chessRow = flippedRowIndex + 1;
        const chessNotation = `${chessCol}${chessRow}`;

        label.textContent = `${flippedRowIndex},${colIndex}`;

        // Create SELECTION SYSTEM coordinate label (RED - WHAT SELECTION THINKS)
        const selectionLabel = document.createElement("div");
        selectionLabel.className = "selection-coord-label";
        selectionLabel.style.cssText = `
          position: absolute;
          bottom: 5px;
          right: 5px;
          background: rgba(255, 0, 0, 0.8);
          color: white;
          font-size: 10px;
          font-weight: bold;
          padding: 2px 4px;
          border-radius: 3px;
          z-index: 1000;
          pointer-events: none;
          font-family: monospace;
        `;

        // What the selection system thinks this square's coordinates are
        // COPY THE YELLOW TAGS - USE SAME FLIPPED ROW INDEXING
        const selectionRow = flippedRowIndex; // Use same flipped row as yellow tags
        const selectionCol = colIndex;
        selectionLabel.textContent = `SEL:${selectionRow},${selectionCol}`;

        // Create TRAVEL SYSTEM coordinate label (BLUE - WHAT TRAVEL SQUARE SYSTEM THINKS)
        const travelLabel = document.createElement("div");
        travelLabel.className = "travel-coord-label";
        travelLabel.style.cssText = `
          position: absolute;
          top: 5px;
          right: 5px;
          background: rgba(0, 100, 255, 0.8);
          color: white;
          font-size: 10px;
          font-weight: bold;
          padding: 2px 4px;
          border-radius: 3px;
          z-index: 1000;
          pointer-events: none;
          font-family: monospace;
        `;

        // What the travel square system thinks this square's coordinates are
        // TEST: Check if findSquareElement would find THIS square when given different coordinates
        let travelRow = -1,
          travelCol = -1;

        // Test different coordinate combinations to see which one would select this exact DOM element
        for (let testRow = 0; testRow <= 7; testRow++) {
          for (let testCol = 0; testCol <= 4; testCol++) {
            const testPosition = { row: testRow, col: testCol };
            // Simulate the findSquareElement logic to see if it would return this square
            const testVisualRowIndex = 7 - testRow;
            if (testVisualRowIndex === rowIndex && testCol === colIndex) {
              travelRow = testRow;
              travelCol = testCol;
              break;
            }
          }
          if (travelRow !== -1) break;
        }

        travelLabel.textContent = `TRV:${travelRow},${travelCol}`;


        // Make sure square has relative positioning
        const squareStyle = getComputedStyle(square);
        if (squareStyle.position === "static") {
          square.style.position = "relative";
        }

        square.appendChild(label);
        square.appendChild(selectionLabel);
        square.appendChild(travelLabel);
      });
    });

  }

  /**
   * Get screen coordinates for a square using chess notation (A3, B4, etc.)
   */
  public getSquareScreenCoordsFromNotation(notation: string): {
    x: number;
    y: number;
  } {
    const position = PieceRulesAnimator.chessNotationToPosition(notation);
    return this.getSquareScreenCoords(position);
  }

  /**
   * Create click animation using chess notation (A3, B4, etc.)
   */
  public async createClickAnimationAtNotation(notation: string): Promise<void> {
    const position = PieceRulesAnimator.chessNotationToPosition(notation);
    const coords = this.getSquareScreenCoords(position);


    // Play click sound
    try {
      this.clickSound.currentTime = 0;
      await this.clickSound.play();
    } catch (error) {
    }

    this.createClickAnimation(coords.x, coords.y);
  }

  /**
   * Create rollover animation using chess notation (A3, B4, etc.)
   */
  public createRolloverAnimationAtNotation(notation: string): void {
    const position = PieceRulesAnimator.chessNotationToPosition(notation);
    this.createRolloverEffect(position);
  }

  /**
   * Helper methods to create animation steps using chess notation
   */
  public static createClickStep(notation: string): AnimationStep {
    const position = PieceRulesAnimator.chessNotationToPosition(notation);
    return {
      type: "click",
      data: {
        fromPosition: position,
      },
    };
  }

  public static createRolloverStep(
    notation: string,
    duration: number = 500
  ): AnimationStep {
    const position = PieceRulesAnimator.chessNotationToPosition(notation);
    return {
      type: "rollover",
      data: {
        toPosition: position,
        duration: duration,
      },
    };
  }

  public static createMoveStep(
    fromNotation: string,
    toNotation: string,
    duration: number = 1500,
    soundEffect: "placement" | "movement" | "capture" | "none" = "movement"
  ): AnimationStep {
    const fromPosition =
      PieceRulesAnimator.chessNotationToPosition(fromNotation);
    const toPosition = PieceRulesAnimator.chessNotationToPosition(toNotation);
    return {
      type: "move",
      data: {
        fromPosition: fromPosition,
        toPosition: toPosition,
        duration: duration,
        soundEffect: soundEffect,
      },
    };
  }

  /**
   * Find the actual piece image element at a board position
   */
  private findPieceImageElement(position: Position): HTMLElement | null {
    // Since we know the target piece from the animation step, let's find it by piece type and side
    const gameState = this.engine.getGameState();
    const square = gameState.board[position.row][position.col];

    if (!square || !square.Pieces || square.Pieces.length === 0) {
      return null;
    }

    // Get the piece at this position
    const targetPiece = square.Pieces[0]; // Assuming we want the first piece
    const pieceType = targetPiece.type.toLowerCase();
    const side = targetPiece.isLight ? "light" : "dark";

    // Look for img elements with src containing the piece type and side
    const allImages = document.querySelectorAll(
      "img"
    ) as NodeListOf<HTMLImageElement>;
    for (const img of allImages) {
      if (img.src && img.src.includes(`${pieceType}-${side}`)) {
        return img as HTMLElement;
      }
      // Also check alt text as a fallback
      if (img.alt && img.alt.toLowerCase() === pieceType) {
        return img as HTMLElement;
      }
    }

    return null;
  }

  /**
   * Get center position of a specific element
   */
  private getPositionFromElement(element: HTMLElement): {
    x: number;
    y: number;
  } {
    const rect = element.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }

  /**
   * Calculate position from container element
   */
  private getPositionFromContainer(
    container: HTMLElement,
    position: Position
  ): { x: number; y: number } {
    const rect = container.getBoundingClientRect();
    const squareSize = Math.min(rect.width, rect.height) / 8;

    // Convert from our coordinate system to screen position
    const x = rect.left + position.col * squareSize + squareSize / 2;
    const y = rect.top + position.row * squareSize + squareSize / 2;

    return { x, y };
  }

  /**
   * Fallback positioning when DOM elements aren't found
   */
  private fallbackPositioning(position: Position): { x: number; y: number } {
    // Use the original hardcoded approach as fallback
    const boardSize = 800;
    const squareSize = boardSize / 8;
    const boardOffsetX = window.innerWidth / 2 - boardSize / 2;
    const boardOffsetY = window.innerHeight / 2 - boardSize / 2;

    const x = boardOffsetX + position.col * squareSize + squareSize / 2;
    const y = boardOffsetY + position.row * squareSize + squareSize / 2;

    return { x, y };
  }

  /**
   * Create visual click animation at screen coordinates
   */
  private createClickAnimation(x: number, y: number): void {
    // Create container element
    const clickContainer = document.createElement("div");
    clickContainer.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 10000;
      width: 40px;
      height: 40px;
      transform: translate(-50%, -50%);
      left: ${x}px;
      top: ${y}px;
    `;

    // Create expanding ring
    const ring = document.createElement("div");
    ring.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 20px;
      height: 20px;
      border: 3px solid #ffd700;
      border-radius: 50%;
      opacity: 1;
      animation: clickExpand 1.2s ease-out forwards;
    `;

    // Create blip
    const blip = document.createElement("div");
    blip.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 8px;
      height: 8px;
      background: #ffd700;
      border-radius: 50%;
      opacity: 1;
      box-shadow: 0 0 10px #ffd700;
      animation: clickBlip 0.6s ease-out forwards;
    `;

    // Add keyframe animations to document if not already present
    this.ensureClickAnimationStyles();

    // Add to container
    clickContainer.appendChild(ring);
    clickContainer.appendChild(blip);

    // Add to document
    document.body.appendChild(clickContainer);

    // Remove after animation completes
    setTimeout(() => {
      if (document.body.contains(clickContainer)) {
        document.body.removeChild(clickContainer);
      }
    }, 1300);
  }

  /**
   * Ensure click animation keyframes are available in document
   */
  private ensureClickAnimationStyles(): void {
    if (document.getElementById("click-animation-styles")) {
      return; // Already added
    }

    const style = document.createElement("style");
    style.id = "click-animation-styles";
    style.textContent = `
      @keyframes clickExpand {
        0% {
          width: 20px;
          height: 20px;
          opacity: 1;
          border-width: 3px;
        }
        50% {
          width: 60px;
          height: 60px;
          opacity: 0.7;
          border-width: 2px;
        }
        100% {
          width: 100px;
          height: 100px;
          opacity: 0;
          border-width: 1px;
        }
      }
      
      @keyframes clickBlip {
        0% {
          width: 8px;
          height: 8px;
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }
        50% {
          width: 12px;
          height: 12px;
          opacity: 0.8;
          transform: translate(-50%, -50%) scale(1.2);
        }
        100% {
          width: 4px;
          height: 4px;
          opacity: 0;
          transform: translate(-50%, -50%) scale(0.5);
        }
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Add selection highlight (this would integrate with the UI system)
   */
  private addSelectionHighlight(position: Position): void {
    // This would need to integrate with the Square component to add 'selected' class
    // Implementation would depend on how we integrate with the board UI
  }

  /**
   * Add movement highlighting
   */
  private addMovementHighlight(from: Position, to: Position): void {
    // This would need to integrate with the Square component to add potential move highlights
    // Implementation would show green highlight on destination square
  }

  /**
   * Add red glow capture effect
   */
  private addCaptureHighlight(position: Position): void {
    // This would use the potentialMoveCapture CSS class for red highlighting
    // Implementation would add red glow effect using existing CSS
  }

  /**
   * Remove all visual highlights including side effects
   */
  private removeAllHighlights(): void {

    // Clear regular highlights and side effects by updating React state
    // NOTE: Side effects are derived from potential moves, so clearing moves should clear side effects too
    this.updateSelectedPiece(undefined);
    this.updatePotentialMoves([]); // This should automatically clear side effects

    // Clear side effect highlights explicitly if callback is available
    if (this.updatePotentialSideEffects) {
      this.updatePotentialSideEffects([]);
    }

    // Force a game state update to ensure all highlights are cleared (but keep entangled states)
    try {
      const gameState = this.engine.getGameState();
      this.updateGameState(gameState);
    } catch (error) {
    }

  }

  /**
   * Utility method for delays with cancellation support
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        resolve();
      }, ms);

      // Check for cancellation every 100ms
      const checkCancel = () => {
        if (this.cancelRequested) {
          clearTimeout(timeout);
          resolve();
          return;
        }
        if (ms > 0) {
          setTimeout(checkCancel, Math.min(100, ms));
        }
      };

      checkCancel();
    });
  }

  /**
   * Check if currently animating (blocks user input)
   */
  public isCurrentlyAnimating(): boolean {
    return this.isAnimating;
  }

  /**
   * Set the post-move description to be shown after movement
   */
  public setPostMoveDescription(description: string | null): void {
    this.currentPostMoveDescription = description;
  }

  /**
   * Get the current post-move description
   */
  public getPostMoveDescription(): string | null {
    return this.currentPostMoveDescription;
  }

  /**
   * Stop current animation sequence immediately and force cleanup
   */
  public stopAnimation(): void {

    // Force stop regardless of current state
    this.cancelRequested = true;
    this.isAnimating = false;
    this.currentSequence = null;
    this.currentStepIndex = 0;
    this.selectedSquare = null;

    // Clear all UI state immediately
    this.updateSelectedPiece(undefined);
    this.updatePotentialMoves([]);
    this.removeAllHighlights();

    // Clean up any CSS overrides
    this.cleanupZombificationCSS();

    // Clean up any persistent piece states (like entangled)
    this.cleanupPersistentPieceStates();

    // DON'T clear post-move description here - it should persist until new animation sets it
    // Only clear the scenario description, not the post-move description
    if (this.updateMoveDescription) {
      this.updateMoveDescription(null);
    }

  }

  /**
   * Update game mode (important for enabling/disabling animator)
   */
  public updateGameMode(mode: Mode): void {
    this.gameMode = mode;
    if (mode !== Mode.Rules && this.isAnimating) {
      this.stopAnimation();
    }
  }
}

// Animation database has been moved to AnimationScenarios.ts for easier editing

export default PieceRulesAnimator;
